import { useRef, useState, useCallback, useEffect } from 'react';
import { PageNode, Connection, ConnectionSide, PageType } from '@/types/sitemap';
import { PageNodeCard } from './PageNodeCard';
import { ConnectionLines } from './ConnectionLines';
import { Minimap } from './Minimap';
import { ZoomIn, ZoomOut, Maximize2, Eye, EyeOff } from 'lucide-react';

interface Props {
  pages: PageNode[];
  connections: Connection[];
  selectedPageIds: string[];
  onSelectPage: (id: string | null, additive?: boolean) => void;
  onSelectPages: (ids: string[]) => void;
  onBatchPositionChange: (deltas: { id: string; x: number; y: number }[]) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onRemoveSection: (pageId: string, sectionId: string) => void;
  onDuplicateSection: (pageId: string, sectionId: string) => void;
  onMoveSectionToPage: (fromPageId: string, sectionId: string, toPageId: string, toIndex?: number) => void;
  onReorderSections: (pageId: string, fromIndex: number, toIndex: number) => void;
  onGenerateAllCopy: (pageId: string) => void;
  onStartConnection: (fromId: string, side: ConnectionSide) => void;
  onCompleteConnection: (toId: string, toSide: ConnectionSide) => void;
  onRemoveConnection: (id: string) => void;
  onUpdateConnectionStyle: (id: string, style: Connection['style']) => void;
  onCancelConnection: () => void;
  onChangeColor: (pageId: string, color: string) => void;
  onChangeSectionColor: (pageId: string, sectionId: string, color: string) => void;
  onEditSection: (pageId: string, sectionId: string) => void;
  onEditPage: (pageId: string) => void;
  onCommentSection: (pageId: string, sectionId: string) => void;
  onCommentPage: (pageId: string) => void;
  onAddPage: (type: PageType, name: string, x?: number, y?: number) => void;
  onAddSectionToPage: (sectionName: string) => void;
  onAddSectionToPageById: (pageId: string, sectionName: string) => void;
  onAddPageRelative: (parentId: string, direction: ConnectionSide, type: PageType, name: string) => void;
  onSwapPagePositions: (pageId1: string, pageId2: string) => void;
  connectingFrom: { pageId: string; side: ConnectionSide } | null;
  editingSectionId?: string | null;
  headerContent?: React.ReactNode;
  activeMode: 'sitemap' | 'comment';
}

const NODE_WIDTH = 280;

export function Canvas({
  pages,
  connections,
  selectedPageIds,
  onSelectPage,
  onSelectPages,
  onBatchPositionChange,
  onDelete,
  onDuplicate,
  onRemoveSection,
  onDuplicateSection,
  onMoveSectionToPage,
  onReorderSections,
  onGenerateAllCopy,
  onStartConnection,
  onCompleteConnection,
  onRemoveConnection,
  onUpdateConnectionStyle,
  onCancelConnection,
  onChangeColor,
  onChangeSectionColor,
  onEditSection,
  onEditPage,
  onCommentSection,
  onCommentPage,
  onAddPage,
  onAddSectionToPage,
  onAddSectionToPageById,
  onAddPageRelative,
  onSwapPagePositions,
  connectingFrom,
  editingSectionId,
  headerContent,
  activeMode,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const [draggingConnection, setDraggingConnection] = useState<{ fromId: string; fromSide: ConnectionSide; toX: number; toY: number } | null>(null);
  const [marquee, setMarquee] = useState<{ startX: number; startY: number; currentX: number; currentY: number } | null>(null);
  const marqueeStart = useRef<{ clientX: number; clientY: number } | null>(null);
  const [showMinimap, setShowMinimap] = useState(true);
  const [nodeResizeKey, setNodeResizeKey] = useState(0);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 800, height: 600 });

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Watch page card height changes and reposition connected children
  const prevHeights = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const V_GAP = 40;

    // Get direct bottom-connected children (parent→child via bottom/top connection)
    const getBottomChildren = (pageId: string): string[] => {
      const children: string[] = [];
      for (const conn of connections) {
        if (conn.fromId === pageId && conn.fromSide === 'bottom') {
          children.push(conn.toId);
        }
        if (conn.toId === pageId && conn.toSide === 'bottom') {
          children.push(conn.fromId);
        }
      }
      return children;
    };

    // Get all connected children (any side) for lateral connections
    const getConnectedChildren = (pageId: string): string[] => {
      const children: string[] = [];
      for (const conn of connections) {
        if (conn.fromId === pageId && conn.fromId !== conn.toId) {
          children.push(conn.toId);
        } else if (conn.toId === pageId && conn.fromId !== conn.toId) {
          children.push(conn.fromId);
        }
      }
      return children;
    };

    const getElHeight = (pageId: string): number => {
      const el = document.querySelector(`[data-page-id="${pageId}"]`) as HTMLElement | null;
      return el ? el.offsetHeight : (prevHeights.current.get(pageId) ?? 80);
    };

    const getElWidth = (pageId: string): number => {
      const el = document.querySelector(`[data-page-id="${pageId}"]`) as HTMLElement | null;
      return el ? el.offsetWidth : NODE_WIDTH;
    };

    const ro = new ResizeObserver((entries) => {
      const changedIds: string[] = [];

      for (const entry of entries) {
        const el = entry.target as HTMLElement;
        const pageId = el.getAttribute('data-page-id');
        if (!pageId) continue;

        const newHeight = entry.contentRect.height;
        const oldHeight = prevHeights.current.get(pageId);
        prevHeights.current.set(pageId, newHeight);

        if (oldHeight !== undefined && Math.abs(newHeight - oldHeight) > 1) {
          changedIds.push(pageId);
        }
      }

      if (changedIds.length === 0) return;

      setNodeResizeKey(k => k + 1);

      // Compute absolute target positions by cascading from changed pages
      // Uses the parent's actual bottom edge + V_GAP for consistent spacing
      const updates = new Map<string, { x: number; y: number }>();
      const processed = new Set<string>();

      // Build a map of current computed Y positions (updated as we go)
      const computedY = new Map<string, number>();
      pages.forEach(p => computedY.set(p.id, p.y));

      const cascadeFrom = (parentId: string) => {
        if (processed.has(parentId)) return;
        processed.add(parentId);

        const parentPage = pages.find(p => p.id === parentId);
        if (!parentPage) return;

        const parentY = computedY.get(parentId) ?? parentPage.y;
        const parentH = getElHeight(parentId);
        const parentBottom = parentY + parentH;

        // Process bottom-connected children with absolute positioning
        const bottomChildren = getBottomChildren(parentId);
        for (const childId of bottomChildren) {
          const childPage = pages.find(p => p.id === childId);
          if (!childPage) continue;

          const targetY = parentBottom + V_GAP;
          
          // Only update if position actually changed
          if (Math.abs(childPage.y - targetY) > 1) {
            // Check collision with non-related pages
            const movingIds = new Set<string>([childId, ...getDescendants(childId)]);
            if (!wouldCollide(childId, childPage.x, targetY, movingIds)) {
              computedY.set(childId, targetY);
              updates.set(childId, { x: childPage.x, y: targetY });
              
              // Also cascade to children of this child
              cascadeFrom(childId);
            }
          } else {
            // Position is fine, but still check grandchildren
            cascadeFrom(childId);
          }
        }

        // For non-bottom connections, shift children below by delta
        const allChildren = getConnectedChildren(parentId);
        for (const childId of allChildren) {
          if (bottomChildren.includes(childId)) continue;
          if (processed.has(childId)) continue;
          
          const childPage = pages.find(p => p.id === childId);
          if (!childPage || childPage.y <= parentY) continue;

          // These pages get the delta treatment
          const changedEntry = changedIds.find(id => id === parentId);
          if (changedEntry) {
            // delta is already applied since prevHeights was updated
          }
        }
      };

      // Get all descendants of a page via connections
      const getDescendants = (pageId: string): string[] => {
        const desc: string[] = [];
        const visited = new Set<string>([pageId]);
        const queue = getConnectedChildren(pageId);
        while (queue.length > 0) {
          const id = queue.shift()!;
          if (visited.has(id)) continue;
          visited.add(id);
          desc.push(id);
          queue.push(...getConnectedChildren(id));
        }
        return desc;
      };

      // Check collision
      const COLLISION_PAD = 20;
      const wouldCollide = (
        targetId: string,
        newX: number,
        newY: number,
        movingIds: Set<string>
      ): boolean => {
        const tw = getElWidth(targetId);
        const th = getElHeight(targetId);
        for (const p of pages) {
          if (p.id === targetId || movingIds.has(p.id)) continue;
          const py = computedY.get(p.id) ?? p.y;
          const pw = getElWidth(p.id);
          const ph = getElHeight(p.id);
          if (
            newX < p.x + pw + COLLISION_PAD &&
            newX + tw + COLLISION_PAD > p.x &&
            newY < py + ph + COLLISION_PAD &&
            newY + th + COLLISION_PAD > py
          ) {
            return true;
          }
        }
        return false;
      };

      for (const changedId of changedIds) {
        cascadeFrom(changedId);
      }

      if (updates.size > 0) {
        const batch = Array.from(updates.entries()).map(([id, pos]) => ({ id, ...pos }));
        onBatchPositionChange(batch);
      }
    });

    const cards = document.querySelectorAll('[data-page-id]');
    cards.forEach(card => ro.observe(card));

    return () => ro.disconnect();
  }, [pages, connections, onBatchPositionChange]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only respond to clicks on the canvas background (not on page cards)
    const target = e.target as HTMLElement;
    if (target.closest('[data-page-id]')) return;

    if (connectingFrom) {
      onCancelConnection();
      setDraggingConnection(null);
      return;
    }

    // Middle mouse or space+click for panning (always)
    if (e.button === 1) {
      isPanning.current = true;
      panStart.current = { x: e.clientX, y: e.clientY, offsetX: offset.x, offsetY: offset.y };
      return;
    }

    // Left click on canvas
    if (e.button === 0) {
      // Shift+click starts marquee selection
      if (e.shiftKey) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          const canvasX = (e.clientX - rect.left - offset.x) / zoom;
          const canvasY = (e.clientY - rect.top - offset.y) / zoom;
          setMarquee({ startX: canvasX, startY: canvasY, currentX: canvasX, currentY: canvasY });
          marqueeStart.current = { clientX: e.clientX, clientY: e.clientY };
        }
        return;
      }

      // Regular click: deselect and start panning
      onSelectPage(null);
      isPanning.current = true;
      panStart.current = { x: e.clientX, y: e.clientY, offsetX: offset.x, offsetY: offset.y };
    }
  }, [offset, zoom, connectingFrom, onCancelConnection, onSelectPage]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (draggingConnection) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const x = (e.clientX - rect.left - offset.x) / zoom;
        const y = (e.clientY - rect.top - offset.y) / zoom;
        setDraggingConnection(prev => prev ? { ...prev, toX: x, toY: y } : null);
      }
      return;
    }

    if (marquee) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const canvasX = (e.clientX - rect.left - offset.x) / zoom;
        const canvasY = (e.clientY - rect.top - offset.y) / zoom;
        setMarquee(prev => prev ? { ...prev, currentX: canvasX, currentY: canvasY } : null);
      }
      return;
    }

    if (!isPanning.current) return;
    const dx = e.clientX - panStart.current.x;
    const dy = e.clientY - panStart.current.y;
    setOffset({ x: panStart.current.offsetX + dx, y: panStart.current.offsetY + dy });
  }, [draggingConnection, marquee, offset, zoom]);

  const handleMouseUp = useCallback(() => {
    isPanning.current = false;

    if (marquee) {
      // Calculate which pages are inside the marquee
      const minX = Math.min(marquee.startX, marquee.currentX);
      const maxX = Math.max(marquee.startX, marquee.currentX);
      const minY = Math.min(marquee.startY, marquee.currentY);
      const maxY = Math.max(marquee.startY, marquee.currentY);

      const selected = pages.filter(page => {
        const el = document.querySelector(`[data-page-id="${page.id}"]`) as HTMLElement | null;
        const w = el ? el.offsetWidth : NODE_WIDTH;
        const h = el ? el.offsetHeight : 80;
        // Check if page rect intersects marquee rect
        return page.x + w > minX && page.x < maxX && page.y + h > minY && page.y < maxY;
      });

      if (selected.length > 0) {
        onSelectPages(selected.map(p => p.id));
      }
      setMarquee(null);
      marqueeStart.current = null;
      return;
    }

    if (draggingConnection) {
      setDraggingConnection(null);
      onCancelConnection();
    }
  }, [draggingConnection, marquee, pages, onCancelConnection, onSelectPages]);

  const handleStartConnection = useCallback((pageId: string, side: ConnectionSide, e: React.MouseEvent) => {
    onStartConnection(pageId, side);
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const x = (e.clientX - rect.left - offset.x) / zoom;
      const y = (e.clientY - rect.top - offset.y) / zoom;
      setDraggingConnection({ fromId: pageId, fromSide: side, toX: x, toY: y });
    }
  }, [onStartConnection, offset, zoom]);

  const handleCompleteConnection = useCallback((pageId: string, side: ConnectionSide) => {
    onCompleteConnection(pageId, side);
    setDraggingConnection(null);
  }, [onCompleteConnection]);

  const handleZoom = (delta: number) => {
    setZoom(z => Math.max(0.2, Math.min(3, z + delta)));
  };

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(z => Math.max(0.2, Math.min(3, z + delta)));
    }
  }, []);

  const fitToView = useCallback(() => {
    if (pages.length === 0) {
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      return;
    }
    const PADDING = 80;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of pages) {
      const el = document.querySelector(`[data-page-id="${p.id}"]`) as HTMLElement | null;
      const w = el ? el.offsetWidth : NODE_WIDTH;
      const h = el ? el.offsetHeight : 120;
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + w);
      maxY = Math.max(maxY, p.y + h);
    }
    const contentW = maxX - minX;
    const contentH = maxY - minY;
    const { width: cw, height: ch } = containerSize;
    const newZoom = Math.min(Math.max((cw - PADDING * 2) / contentW, 0.2), Math.min((ch - PADDING * 2) / contentH, 3));
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    setZoom(newZoom);
    setOffset({
      x: cw / 2 - centerX * newZoom,
      y: ch / 2 - centerY * newZoom,
    });
  }, [pages, containerSize]);

  // Marquee rect in canvas coordinates
  const marqueeRect = marquee ? {
    x: Math.min(marquee.startX, marquee.currentX),
    y: Math.min(marquee.startY, marquee.currentY),
    width: Math.abs(marquee.currentX - marquee.startX),
    height: Math.abs(marquee.currentY - marquee.startY),
  } : null;

  return (
    <div
      ref={outerRef}
      className="flex-1 relative overflow-hidden bg-canvas canvas-grid"
      onWheel={handleWheel}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('application/sitemapper-page') || e.dataTransfer.types.includes('application/sitemapper-component')) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }
      }}
      onDrop={(e) => {
        const pageData = e.dataTransfer.getData('application/sitemapper-page');
        const compData = e.dataTransfer.getData('application/sitemapper-component');
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect && pageData) {
          const parsed = JSON.parse(pageData);
          const x = (e.clientX - rect.left - offset.x) / zoom;
          const y = (e.clientY - rect.top - offset.y) / zoom;
          onAddPage(parsed.type, parsed.label, x, y);
        } else if (compData && selectedPageIds.length === 1) {
          onAddSectionToPage(compData);
        }
      }}
    >
      {/* Floating header */}
      {headerContent && (
        <div className="absolute top-4 left-4 right-4 z-30 pointer-events-none">
          <div className="pointer-events-auto">
            {headerContent}
          </div>
        </div>
      )}

      <div className="absolute bottom-4 right-4 z-20 flex flex-col items-end gap-2">
        {showMinimap && (
          <Minimap
            pages={pages}
            selectedPageIds={selectedPageIds}
            offset={offset}
            zoom={zoom}
            containerWidth={containerSize.width}
            containerHeight={containerSize.height}
            onNavigate={setOffset}
            className="w-full"
          />
        )}
        <div ref={toolbarRef} className="flex items-center gap-1 bg-card/95 backdrop-blur rounded-xl node-shadow p-1.5">
          <button onClick={() => handleZoom(-0.15)} className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <ZoomOut size={20} />
          </button>
          <input
            type="text"
            value={`${Math.round(zoom * 100)}%`}
            onChange={(e) => {
              const val = parseInt(e.target.value.replace('%', ''), 10);
              if (!isNaN(val)) {
                setZoom(Math.max(0.2, Math.min(3, val / 100)));
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                (e.target as HTMLInputElement).blur();
              }
            }}
            onFocus={(e) => {
              e.target.value = `${Math.round(zoom * 100)}`;
              e.target.select();
            }}
            onBlur={(e) => {
              e.target.value = `${Math.round(zoom * 100)}%`;
            }}
            className="text-sm font-mono text-foreground px-2 min-w-[3.5rem] w-16 text-center font-medium bg-transparent border-none outline-none focus:bg-muted rounded-md"
          />
          <button onClick={() => handleZoom(0.15)} className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <ZoomIn size={20} />
          </button>
          <div className="w-px h-5 bg-border mx-1" />
          <button onClick={fitToView} className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <Maximize2 size={20} />
          </button>
          <div className="w-px h-5 bg-border mx-1" />
          <button onClick={() => setShowMinimap(prev => !prev)} className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            {showMinimap ? <Eye size={20} /> : <EyeOff size={20} />}
          </button>
        </div>
      </div>

      {connectingFrom && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-20 bg-accent text-accent-foreground text-xs font-medium px-4 py-2 rounded-full shadow-lg">
          Click a port on another page to connect · Press Esc to cancel
        </div>
      )}

      <div
        ref={containerRef}
        className="w-full h-full cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            position: 'relative',
            width: '5000px',
            height: '5000px',
          }}
        >
          <ConnectionLines
            pages={pages}
            connections={connections}
            selectedPageIds={selectedPageIds}
            onRemoveConnection={onRemoveConnection}
            onChangeStyle={onUpdateConnectionStyle}
            draggingConnection={draggingConnection}
            renderKey={nodeResizeKey}
          />

          {/* Marquee selection rectangle */}
          {marqueeRect && (
            <div
              className="absolute border-2 border-primary/50 bg-primary/10 rounded-sm pointer-events-none"
              style={{
                left: marqueeRect.x,
                top: marqueeRect.y,
                width: marqueeRect.width,
                height: marqueeRect.height,
              }}
            />
          )}

          {pages.map(page => (
            <PageNodeCard
              key={page.id}
              page={page}
              isSelected={selectedPageIds.includes(page.id)}
              isConnecting={!!connectingFrom}
              isConnectSource={connectingFrom?.pageId === page.id}
              allPages={pages}
              onBatchPositionChange={onBatchPositionChange}
              onSelect={(id, additive) => onSelectPage(id, additive)}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onRemoveSection={onRemoveSection}
              onDuplicateSection={onDuplicateSection}
              onMoveSectionToPage={onMoveSectionToPage}
              onReorderSections={onReorderSections}
              onGenerateAllCopy={onGenerateAllCopy}
              onStartConnection={handleStartConnection}
              onCompleteConnection={handleCompleteConnection}
              onChangeColor={onChangeColor}
              onChangeSectionColor={onChangeSectionColor}
              onEditSection={onEditSection}
              onAddPageRelative={onAddPageRelative}
              onSwapPagePositions={onSwapPagePositions}
              onAddSection={onAddSectionToPageById}
              onEditPage={onEditPage}
              onCommentSection={onCommentSection}
              onCommentPage={onCommentPage}
              activeMode={activeMode}
              connections={connections}
              zoom={zoom}
              editingSectionId={editingSectionId}
            />
          ))}
        </div>
      </div>

    </div>
  );
}
